import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ClockCircleOutlined,
  DeleteOutlined,
  PlaySquareOutlined,
  PlusOutlined,
  ReloadOutlined,
  StarFilled,
  VideoCameraOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  cinemaApi,
  CinemaMovie,
  CinemaMoviePayload,
} from '../../api/cinema';

const { Title, Text } = Typography;
const { TextArea } = Input;

const POSTER_FALLBACK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 300"><rect width="200" height="300" fill="var(--l-text)"/><text x="100" y="155" font-family="Inter, sans-serif" font-size="14" fill="var(--l-muted)" text-anchor="middle">Нет постера</text></svg>`,
  );

interface MovieFormValues {
  title: string;
  description?: string;
  poster_url?: string;
  trailer_url?: string;
  rating?: number | null;
  tags?: string[];
  sessions?: string[];
  show_date?: string | null;
  sort_order?: number;
  is_active?: boolean;
  /** Поля карточки фильма — правятся руками, тянутся из Кинопоиска. */
  director?: string;
  movie_length?: number | null;
  age_rating?: number | null;
}

const CinemaPage: React.FC = () => {
  const [movies, setMovies] = useState<CinemaMovie[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CinemaMovie | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [form] = Form.useForm<MovieFormValues>();
  const [posterPreview, setPosterPreview] = useState<string | null>(null);

  const [messageApi, contextHolder] = message.useMessage();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cinemaApi.list();
      const list = Array.isArray(res.data) ? [...res.data] : [];
      list.sort((a, b) => a.sort_order - b.sort_order);
      setMovies(list);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки фильмов');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRescrape = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await cinemaApi.refresh();
      const list = Array.isArray(res.data.movies) ? [...res.data.movies] : [];
      list.sort((a, b) => a.sort_order - b.sort_order);
      setMovies(list);
      const { created, updated, deleted } = res.data.result || {};
      messageApi.success(
        `Расписание обновлено: +${created ?? 0} / ~${updated ?? 0} / −${
          deleted ?? 0
        }`,
      );
    } catch (err: any) {
      messageApi.error(
        err?.response?.data?.detail || 'Не удалось обновить расписание',
      );
    } finally {
      setRefreshing(false);
    }
  }, [messageApi]);

  const activeCount = useMemo(
    () => movies.filter((m) => m.is_active).length,
    [movies],
  );
  const totalSessions = useMemo(
    () =>
      movies.reduce(
        (sum, m) => sum + (Array.isArray(m.sessions) ? m.sessions.length : 0),
        0,
      ),
    [movies],
  );

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
      sort_order: movies.length,
      tags: [],
      sessions: [],
    });
    setPosterPreview(null);
    setModalOpen(true);
  };

  const openEdit = (movie: CinemaMovie) => {
    setEditing(movie);
    form.setFieldsValue({
      title: movie.title,
      description: movie.description || undefined,
      poster_url: movie.poster_url || undefined,
      trailer_url: movie.trailer_url || undefined,
      rating: movie.rating,
      tags: Array.isArray(movie.tags) ? movie.tags : [],
      sessions: Array.isArray(movie.sessions) ? movie.sessions : [],
      show_date: movie.show_date || undefined,
      sort_order: movie.sort_order,
      is_active: movie.is_active,
      // Поля карточки (30.07.2026). Правятся руками, но при следующем
      // «Обновить из источника» перезапишутся Кинопоиском: upsert заменяет
      // таблицу целиком. Ручная правка — для случая, когда источник врёт.
      director: movie.director || undefined,
      movie_length: movie.movie_length ?? undefined,
      age_rating: movie.age_rating ?? undefined,
    });
    setPosterPreview(movie.poster_url || null);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values: MovieFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const payload: CinemaMoviePayload = {
        title: values.title,
        description: values.description ?? null,
        poster_url: values.poster_url ?? null,
        trailer_url: values.trailer_url ?? null,
        rating: values.rating ?? null,
        tags: values.tags || [],
        sessions: values.sessions || [],
        show_date: values.show_date || null,
        sort_order: values.sort_order ?? 0,
        is_active: values.is_active ?? true,
        director: values.director ?? null,
        movie_length: values.movie_length ?? null,
        age_rating: values.age_rating ?? null,
      };
      if (editing) {
        await cinemaApi.update(editing.id, payload);
        messageApi.success('Фильм обновлён');
      } else {
        await cinemaApi.create(payload);
        messageApi.success('Фильм добавлен');
      }
      setModalOpen(false);
      fetchData();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    setDeleting(true);
    try {
      await cinemaApi.delete(editing.id);
      messageApi.success('Фильм удалён');
      setModalOpen(false);
      fetchData();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка удаления');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {contextHolder}

      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>
            Кинотеатр
          </Title>
          <Text type="secondary">Расписание сеансов в Аксае</Text>
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading && !refreshing}
          >
            Обновить список
          </Button>
          <Button
            icon={<PlaySquareOutlined />}
            onClick={handleRescrape}
            loading={refreshing}
            type="default"
          >
            Спарсить расписание заново
          </Button>
          <Button
            type="primary"
           
            icon={<PlusOutlined />}
            onClick={openCreate}
            style={{ background: 'var(--l-accent)' }}
          >
            Добавить фильм
          </Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Всего фильмов"
              value={movies.length}
              prefix={<VideoCameraOutlined style={{ color: 'var(--l-accent)' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}>
            <Statistic
              title="Активных фильмов"
              value={activeCount}
              valueStyle={{ color: 'var(--l-ok)' }}
              prefix={<PlaySquareOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}>
            <Statistic
              title="Всего сеансов"
              value={totalSessions}
              prefix={<ClockCircleOutlined style={{ color: 'var(--l-accent)' }} />}
            />
          </Card>
        </Col>
      </Row>

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {!loading && movies.length === 0 ? (
        <Card>
          <Empty description="Фильмы ещё не добавлены">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              style={{ background: 'var(--l-accent)' }}
            >
              Добавить первый фильм
            </Button>
          </Empty>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {movies.map((movie) => (
            <Col key={movie.id} xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                onClick={() => openEdit(movie)}
                bodyStyle={{ padding: 12 }}
                style={{
                  overflow: 'hidden',
                  opacity: movie.is_active ? 1 : 0.6,
                  height: '100%',
                }}
                cover={
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      aspectRatio: '2 / 3',
                      overflow: 'hidden',
                      background: 'var(--l-text)',
                    }}
                  >
                    <img
                      alt={movie.title}
                      src={movie.poster_url || POSTER_FALLBACK}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = POSTER_FALLBACK;
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                    {!movie.is_active && (
                      <Tag
                        color="default"
                        style={{ position: 'absolute', top: 8, left: 8 }}
                      >
                        Скрыт
                      </Tag>
                    )}
                    {movie.rating != null && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          background: 'rgba(15, 23, 42, 0.85)',
                          color: '#FCD34D',
                          padding: '3px 8px',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <StarFilled />
                        {Number(movie.rating).toFixed(1)}
                      </div>
                    )}
                  </div>
                }
              >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Text
                    strong
                    style={{
                      fontSize: 15,
                      display: 'block',
                      lineHeight: 1.3,
                      minHeight: 40,
                    }}
                    ellipsis={{ tooltip: movie.title }}
                  >
                    {movie.title}
                  </Text>
                  {Array.isArray(movie.tags) && movie.tags.length > 0 && (
                    <div>
                      {movie.tags.slice(0, 3).map((t) => (
                        <Tag key={t} style={{ marginBottom: 4 }}>
                          {t}
                        </Tag>
                      ))}
                      {movie.tags.length > 3 && (
                        <Tag>+{movie.tags.length - 3}</Tag>
                      )}
                    </div>
                  )}
                  {/* Мета из Кинопоиска — год, хронометраж, ценз, режиссёр.
                      Показываем ИМЕННО ЗДЕСЬ, чтобы редактор видел, что уехало
                      в приложение: раньше эти поля тянулись, но нигде не были
                      видны, и проверить их было нечем. Каждое условное —
                      заполнено не у всех фильмов (ценз 5 из 9). */}
                  {(movie.release_year ||
                    movie.movie_length ||
                    movie.age_rating != null) && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {[
                        movie.release_year ? String(movie.release_year) : null,
                        movie.movie_length
                          ? `${Math.floor(movie.movie_length / 60)} ч ${movie.movie_length % 60} мин`
                          : null,
                        movie.age_rating != null ? `${movie.age_rating}+` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                  )}
                  {movie.director && (
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: 'block' }}
                      ellipsis={{ tooltip: movie.director }}
                    >
                      реж. {movie.director}
                    </Text>
                  )}
                  {Array.isArray(movie.actors) && movie.actors.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {movie.actors.slice(0, 5).map((a) => (
                        <img
                          key={a.photo}
                          src={a.photo}
                          alt={a.name}
                          title={a.name}
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: 13,
                            objectFit: 'cover',
                            border: '1.5px solid #fff',
                            marginLeft: -6,
                          }}
                        />
                      ))}
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                        {movie.actors.length} актёров
                      </Text>
                    </div>
                  )}
                  <Space size={6} wrap>
                    <Tag
                      icon={<ClockCircleOutlined />}
                    >
                      {Array.isArray(movie.sessions)
                        ? movie.sessions.length
                        : 0}{' '}
                      сеансов
                    </Tag>
                    {/* Залы — из `sessions_detail`. Показываем сколько разных
                        задействовано: сразу видно, что зал вообще пришёл. */}
                    {Array.isArray(movie.sessions_detail) &&
                      movie.sessions_detail.some((x) => x.hall != null) && (
                        <Tag>
                          {`залы ${Array.from(
                            new Set(
                              movie.sessions_detail
                                .map((x) => x.hall)
                                .filter((h): h is number => h != null),
                            ),
                          )
                            .sort((a, b) => a - b)
                            .join(', ')}`}
                        </Tag>
                      )}
                    {Array.isArray(movie.sessions_detail) &&
                      movie.sessions_detail.some((x) => x.is3d) && (
                        <Tag>3D</Tag>
                      )}
                    {movie.is_active && <Tag color="green">Активен</Tag>}
                  </Space>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title={editing ? 'Редактировать фильм' : 'Добавить фильм'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={880}
        destroyOnClose
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div>
              {editing && (
                <Popconfirm
                  title="Удалить фильм?"
                  description="Действие нельзя отменить"
                  okText="Удалить"
                  okButtonProps={{ danger: true }}
                  cancelText="Отмена"
                  onConfirm={handleDelete}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={deleting}
                  >
                    Удалить
                  </Button>
                </Popconfirm>
              )}
            </div>
            <Space>
              <Button onClick={() => setModalOpen(false)}>Отмена</Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={submitting}
                style={{ background: 'var(--l-accent)' }}
              >
                {editing ? 'Сохранить' : 'Создать'}
              </Button>
            </Space>
          </div>
        }
      >
        <Row gutter={24} style={{ marginTop: 8 }}>
          <Col xs={24} md={9}>
            <div
              style={{
                width: '100%',
                aspectRatio: '2 / 3',
                background: 'var(--l-text)',
                borderRadius: 8,
                overflow: 'hidden',
                marginBottom: 12,
              }}
            >
              <img
                alt="poster preview"
                src={posterPreview || POSTER_FALLBACK}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = POSTER_FALLBACK;
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </div>
            {editing?.updated_at && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Обновлено:{' '}
                {dayjs(editing.updated_at).format('DD.MM.YYYY HH:mm')}
              </Text>
            )}
          </Col>
          <Col xs={24} md={15}>
            <Form
              form={form}
              layout="vertical"
              onValuesChange={(changed) => {
                if ('poster_url' in changed) {
                  setPosterPreview(changed.poster_url || null);
                }
              }}
            >
              <Form.Item
                name="title"
                label="Название"
                rules={[{ required: true, message: 'Укажите название' }]}
              >
                <Input placeholder="Например: Главный герой" />
              </Form.Item>

              <Form.Item name="description" label="Описание">
                <TextArea
                  rows={3}
                  placeholder="Короткий синопсис, 1-3 предложения"
                />
              </Form.Item>

              <Row gutter={12}>
                <Col span={16}>
                  <Form.Item name="poster_url" label="Ссылка на постер">
                    <Input placeholder="https://..." />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="rating" label="Рейтинг">
                    <InputNumber
                      style={{ width: '100%' }}
                      step={0.1}
                      precision={1}
                      min={0}
                      max={10}
                      placeholder="7.8"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="trailer_url" label="Ссылка на трейлер">
                <Input placeholder="https://youtube.com/..." />
              </Form.Item>

              {/* Поля из Кинопоиска. Актёров руками не правим: их 12 на фильм,
                  тянутся с фото автоматически и заполнены у всех 9 фильмов
                  проката. Здесь то, что источник иногда не отдаёт (ценз — у 5
                  из 9) или отдаёт неверно. */}
              <Row gutter={12}>
                <Col span={10}>
                  <Form.Item name="director" label="Режиссёр">
                    <Input placeholder="Кристофер Нолан" />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item
                    name="movie_length"
                    label="Длительность, мин"
                    tooltip="В приложении показывается как «2 ч 52 мин»"
                  >
                    <InputNumber style={{ width: '100%' }} min={1} max={600} placeholder="172" />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item name="age_rating" label="Возраст" tooltip="0 / 6 / 12 / 16 / 18">
                    <InputNumber style={{ width: '100%' }} min={0} max={21} placeholder="18" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="tags"
                label="Теги / жанры"
                tooltip="Нажмите Enter после каждого тега"
              >
                <Select
                  mode="tags"
                  placeholder="Боевик, Драма, 16+"
                  tokenSeparators={[',']}
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                name="sessions"
                label="Сеансы"
                tooltip="Нажмите Enter после каждого времени"
              >
                <Select
                  mode="tags"
                  placeholder="14:40, 17:30"
                  tokenSeparators={[',', ' ']}
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name="show_date" label="Дата показа">
                    <Input placeholder="2026-04-18 или период" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="sort_order" label="Порядок">
                    <InputNumber
                      style={{ width: '100%' }}
                      step={1}
                      precision={0}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="is_active"
                    label="Активен"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Да" unCheckedChildren="Нет" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default CinemaPage;
